import { CloseButton as MantineCloseButton, UnstyledButton as MantineUnstyledButton } from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "./style-props";

export interface UnstyledButtonProps extends ElementProps<"button">, StyleProps {
  children?: React.ReactNode;
}

/**
 * Same polymorphic flattening as `BadgeRoot` in the indicators module: Mantine types `UnstyledButton`
 * as a polymorphic union whose `component` arm selects the element prop set, and a non-generic
 * wrapper cannot restate that. `component` is not declared because no call site renders it as
 * anything but its default `<button>` root. `buttons-casts.test.ts` pins the prop keys the cast
 * routes to Mantine.
 */
const UnstyledButtonRoot = MantineUnstyledButton as React.ComponentType<
  UnstyledButtonProps & { readonly ref?: React.Ref<HTMLButtonElement> }
>;

export const UnstyledButton = React.forwardRef<HTMLButtonElement, UnstyledButtonProps>(function UnstyledButton(
  { children, ...props },
  ref,
) {
  return (
    <UnstyledButtonRoot {...props} ref={ref}>
      {children}
    </UnstyledButtonRoot>
  );
});

export interface CloseButtonProps extends ElementProps<"button">, StyleProps {
  /** The only variant in live use; widen when a call site needs another. */
  variant?: "transparent";
  size?: SizeValue;
  radius?: SizeValue;
  color?: string;
  /** Width and height of the X icon, independent of the button `size`. */
  iconSize?: number | string;
}

/**
 * Same polymorphic flattening as `UnstyledButtonRoot` above. The root takes Mantine 7's `icon` in place
 * of the owned `children` and no `color` — Mantine 7's CloseButton has no color prop — which the
 * wrapper translates below; `buttons-casts.test.ts` pins both translations to Mantine's own types.
 */
const CloseButtonRoot = MantineCloseButton as React.ComponentType<
  Omit<CloseButtonProps, "children" | "color"> & { icon?: React.ReactNode; readonly ref?: React.Ref<HTMLButtonElement> }
>;

/** Mantine 6's X icon width and height per button size key, in px. */
const CLOSE_ICON_SIZES: Partial<Record<string, number>> = { xs: 12, sm: 16, md: 20, lg: 28, xl: 34 };

/**
 * Mantine 6's CloseButton was a `gray` ActionIcon: `size` defaulted to `sm`, the icon took the
 * per-size width above unless `iconSize` was set, `color` tinted the icon, and `children` replaced the
 * icon. Mantine 7 defaults to `md` with a 70% icon, drops `color`, and renders `children` after the
 * icon. The wrapper keeps Mantine 6's behaviour (SC-2703): the defaults are restated, `color` becomes
 * the `c` text color (an explicit `c` still wins), and `children` becomes Mantine 7's `icon`.
 */
export const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(function CloseButton(
  { children, color = "gray", c, size = "sm", iconSize, ...props },
  ref,
) {
  return (
    <CloseButtonRoot
      {...props}
      c={c ?? color}
      size={size}
      iconSize={iconSize ?? (typeof size === "string" ? CLOSE_ICON_SIZES[size] : undefined)}
      icon={children}
      ref={ref}
    />
  );
});
